import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, setDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

const DEFAULT_STATS = {
  totalUsers: 0,
  totalProperties: 0,
  totalSearches: 0,
  totalAdmins: 0,
  lastUpdated: null
};

const DEFAULT_CACHE_METRICS = {
  totalHits: 0,
  totalMisses: 0,
  hitRate: 0,
  lastUpdated: null
};

export const useAnalytics = (isAdmin) => {
  const [globalStats, setGlobalStats] = useState(DEFAULT_STATS);
  const [cacheMetrics, setCacheMetrics] = useState(DEFAULT_CACHE_METRICS);
  const [isLoading, setIsLoading] = useState(true);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [error, setError] = useState(null);

  // Listen to global stats document
  useEffect(() => {
    if (!isAdmin || !db) {
      setIsLoading(false);
      return;
    }

    const statsRef = doc(db, 'artifacts/bz-tracker/analytics/globalStats');

    const unsubscribe = onSnapshot(
      statsRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setGlobalStats({
            totalUsers: data.totalUsers || 0,
            totalProperties: data.totalProperties || 0,
            totalSearches: data.totalSearches || 0,
            totalAdmins: data.totalAdmins || 0,
            lastUpdated: data.lastUpdated?.toDate?.() || null
          });
        } else {
          setGlobalStats(DEFAULT_STATS);
        }
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching analytics:', err);
        setError(err.message);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isAdmin]);

  // Listen to cache metrics document
  useEffect(() => {
    if (!isAdmin || !db) return;

    const cacheMetricsRef = doc(db, 'artifacts/bz-tracker/analytics/cacheMetrics');

    const unsubscribe = onSnapshot(
      cacheMetricsRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const totalHits = data.totalHits ?? 0;
          const totalMisses = data.totalMisses ?? 0;
          const total = totalHits + totalMisses;
          setCacheMetrics({
            totalHits,
            totalMisses,
            hitRate: total > 0 ? totalHits / total : 0,
            lastUpdated: data.lastUpdated?.toDate?.() || null
          });
        } else {
          setCacheMetrics(DEFAULT_CACHE_METRICS);
        }
      },
      (err) => {
        console.error('Error fetching cache metrics:', err);
      }
    );

    return () => unsubscribe();
  }, [isAdmin]);

  // Recalculate stats by iterating all users
  const recalculateStats = useCallback(async () => {
    if (!isAdmin || !db) return;

    setIsRecalculating(true);
    setError(null);

    try {
      const usersRef = collection(db, 'artifacts/bz-tracker/users');
      const usersSnapshot = await getDocs(usersRef);

      let totalUsers = 0;
      let totalProperties = 0;
      let totalSearches = 0;
      let totalAdmins = 0;

      // Iterate through all users
      for (const userDoc of usersSnapshot.docs) {
        totalUsers++;
        const userData = userDoc.data();

        if (userData.isAdmin) {
          totalAdmins++;
        }

        // Count properties
        try {
          const propsRef = collection(db, `artifacts/bz-tracker/users/${userDoc.id}/trackedProperties`);
          const propsSnapshot = await getDocs(propsRef);
          totalProperties += propsSnapshot.size;
        } catch (e) {
          // Subcollection might not exist
        }

        // Count searches
        try {
          const searchesRef = collection(db, `artifacts/bz-tracker/users/${userDoc.id}/trackedSearches`);
          const searchesSnapshot = await getDocs(searchesRef);
          totalSearches += searchesSnapshot.size;
        } catch (e) {
          // Subcollection might not exist
        }
      }

      // Save to Firestore
      const statsRef = doc(db, 'artifacts/bz-tracker/analytics/globalStats');
      await setDoc(statsRef, {
        totalUsers,
        totalProperties,
        totalSearches,
        totalAdmins,
        lastUpdated: serverTimestamp()
      });

      return { totalUsers, totalProperties, totalSearches, totalAdmins };
    } catch (err) {
      console.error('Error recalculating stats:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsRecalculating(false);
    }
  }, [isAdmin]);

  return {
    globalStats,
    cacheMetrics,
    isLoading,
    isRecalculating,
    error,
    recalculateStats
  };
};
